import { Typography, TextSize } from '@shared/index';
import { formatNum } from '@app/utils/math';
import { daysUntilEndOfQuarter } from '@app/utils/date';
import { ProgressGraph } from '../progress-graph';
import { getCurrentDate } from '@shared/utils/getCurrentDate';
import { getQuarterCurrent } from '@app/utils/quarters';
import { EditQuarterlyResult } from '../progress-card/edit-quarterly-result';
import { 
  useGetTeamQuarterlyBonus, 
  useGetTeamQuarterlyBonusResults,
  useUpdateTeamQuarterlyPlan 
} from '@entities/managers-perfomance';
import { QUERY_KEY_MANAGER_PERFORMANCE } from '@entities/managers-perfomance/query-keys';
import { Pencil } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

export const QuarterlyProgressResult = () => {
  const { year } = getCurrentDate();
  const quarter = getQuarterCurrent();
  const [isEditPopupOpen, setIsEditPopupOpen] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const { data: teamQuarterlyBonus, isLoading: isLoadingBonus } = useGetTeamQuarterlyBonus({
    year,
    quarter,
  });

  const { data: teamQuarterlyBonusResults = [], isLoading: isLoadingResults } = useGetTeamQuarterlyBonusResults({
    year,
    quarter,
  });

  const updateTeamQuarterlyPlan = useUpdateTeamQuarterlyPlan();

  const { managersData, totalHashrate } = useMemo(() => {
    if (!teamQuarterlyBonusResults.length) {
      return { managersData: [], totalHashrate: 0 };
    }

    const mapped = teamQuarterlyBonusResults.map(result => ({
      id: result.manager_id,
      name: result.manager_name,
      result: result.hashrate,
    }));

    const total = mapped.reduce((sum, manager) => sum + manager.result, 0);

    return {
      managersData: mapped,
      totalHashrate: total,
    };
  }, [teamQuarterlyBonusResults]);

  const {
    plan = 0,
    hashrate: currentValue = 0,
    percentage: percent = 0,
    remaining_hashrate: remainingValue = 0,
  } = teamQuarterlyBonus ?? {};

  const remainingPercent = plan > 0 ? (remainingValue / plan) * 100 : 0;
  const planValue = plan > 0 ? formatNum(plan) : '';

  const daysLeft = daysUntilEndOfQuarter();

  const infoCards = [
    { label: 'План', value: formatNum(plan), unit: 'Ph' },
    { label: 'Осталось', value: daysLeft, unit: 'дн' },
  ];

  const handleOpenEditPopup = () => {
    setIsEditPopupOpen(true);
  };

  const handleCloseEditPopup = () => {
    setIsEditPopupOpen(false);
  };

  const handleUpdateQuarterlyPlan = async (planValue: string) => {
    const cleanPlan = planValue.replace(/\s/g, '') || '0';

    const bonusManager =
      teamQuarterlyBonus?.is_bonus_active && teamQuarterlyBonus?.bonus_manager_id
        ? teamQuarterlyBonus.bonus_manager_id
        : null;

    await updateTeamQuarterlyPlan.fetch({
      year,
      quarter,
      plan: cleanPlan,
      isBonusActive: teamQuarterlyBonus?.is_bonus_active ?? false,
      bonusManager,
    });

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.TEAM_QUARTERLY_BONUS, year, quarter],
    });
    
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.TEAM_QUARTERLY_HISTORY, year],
    });

    toast.success('Квартальный план команды обновлен');
    setIsEditPopupOpen(false);
  };

  return (
    <div className="flex flex-col gap-8">
      <Typography
        size={TextSize.size24}
        className="font-semibold"
        title="Показатели команды"
      />
      <div className="grid grid-cols-[260px_380px] items-end gap-12">
        <div />
        <div className="justify-self-start">
          <ProgressGraph
            percent={percent}
            currentValue={currentValue}
            plan={plan}
            remainingPercent={remainingPercent}
            remainingPh={formatNum(remainingValue)}
            managers={managersData}
            totalHashrate={totalHashrate}
            quarterly={true}
          />
        </div>
        <div className="flex items-end gap-8">
          <div className='flex items-baseline'>
            <div className="flex flex-col gap-[10px]">
              {infoCards.map(({ label, value, unit }) => (
                <div
                  key={label}
                  className="w-[224px] flex justify-between items-center bg-dark-200 rounded-md px-2 py-1"
                >
                  <Typography size={TextSize.size14} className="text-gray-400" text={`${label}:`} />
                  <div className="flex gap-1 items-start">
                    <Typography size={TextSize.size20} text={value} />
                    <span className="text-xs text-gray-400">{unit}</span>
                  </div>
                </div>
              ))}
            </div>
            <Pencil
              size={16}
              className="text-gray-400 cursor-pointer hover:opacity-70 ml-[42px]"
              onClick={handleOpenEditPopup}
            />
          </div>
        </div>
      </div>
      {isEditPopupOpen && (
        <EditQuarterlyResult
          open={true}
          value={planValue}
          onSubmit={handleUpdateQuarterlyPlan}
          onClose={handleCloseEditPopup}
        />
      )}
    </div>
  );
};